import { useCallback, useRef, useEffect, useMemo } from 'react';
import { atom, getDefaultStore, useAtom, type PrimitiveAtom } from 'jotai';
import { apiFetch, sseUrl } from '@/lib/api';
import { normKey } from '@/lib/assetKeys';

export interface ProductionStage {
  stage: number;
  name: string;
  status: 'pending' | 'active' | 'complete';
  model?: string;
}

export interface ShotProgress {
  shot_id: string;
  step: string;
  current: number;
  total_shots: number;
  attempt: number;
  model?: string;
}

export interface AssetInfo {
  type:
    | 'keyframe'
    | 'clip'
    | 'dialog'
    | 'vo'
    | 'vo_booth'
    | 'music'
    | 'sfx'
    | 'final'
    | 'audio'
    | 'character'
    | 'location'
    | 'prop'
    | 'footage';
  shot_id: string;
  path: string;
  // Authored LOC_nnn anchor (location assets only) — shown so the user can verify
  // the design maps to the location they labeled in the AV script.
  location_id?: string;
}

export interface LogEntry {
  event: string;
  message?: string;
  timestamp: number;
}

export interface CrewActivity {
  role: string;
  phase: string;
  task: string;
}

export interface ChatMessage {
  id: string;
  role: 'producer' | 'director';
  text: string;
  // 'activity' = a live tool step of the Director's current turn (not stored).
  kind: 'message' | 'proposal' | 'answer' | 'result' | 'activity';
  action?: { type: string; target?: string; note?: string; summary?: string } | null;
  asset_ref?: string;
  status?: string;
  timestamp: number;
  // The Director's turn: quick replies, assets it showed, the tool steps it took.
  turn?: string;
  reply_to?: string;
  choices?: string[];
  attachments?: string[];
  steps?: string[];
}

export interface TelemetryData {
  total_duration_seconds: number;
  total_api_calls: number;
  total_estimated_cost_usd: number;
  active_workers?: number;
  peak_workers?: number;
  max_parallel_workers?: number;
  api_breakdown: Record<string, number>;
  phases: Record<
    string,
    {
      duration_seconds: number;
      api_calls: number;
      estimated_cost_usd: number;
      status: string;
    }
  >;
}

/** Live timing for the currently-running phase, derived from shot_progress
 *  events (storyboard, shooting, …). Used to project a per-phase ETA. */
export interface PhaseTiming {
  step: string; // storyboard | shooting | …
  startedTs: number; // SERVER emit time (ms) of the first observed event this phase
  startCurrent: number; // `current` at first observation (may be >1 on reload)
  current: number; // latest item index reached
  total: number; // items in this phase
  updatedTs: number; // SERVER emit time (ms) of the last advance — for the rate
  updatedAt: number; // client clock of the last advance — for the between-event countdown
}

export interface ProductionState {
  id: string | null;
  status: 'idle' | 'starting' | 'running' | 'complete' | 'error';
  stages: ProductionStage[];
  currentStage: number;
  currentPhase: string;
  activeCrew: CrewActivity | null;
  crewHistory: CrewActivity[];
  shotProgress: ShotProgress | null;
  phaseTiming: PhaseTiming | null;
  assets: AssetInfo[];
  logs: LogEntry[];
  error: string | null;
  finalVideo: string | null;
  scriptBreakdown: unknown | null;
  shotPlan: unknown | null;
  telemetry: TelemetryData | null;
  availableAssets: string[];
  chat: ChatMessage[];
  // Normalized "type:ID" keys of assets that received an asset_ready event — i.e.
  // ACCEPTED by QC. Tiles present on disk but not in this set are still iterating.
  finalAssets: Set<string>;
  /** Bumped every time an asset_ready re-fires for an already-known asset — regenerations. */
  assetVersions: Record<string, number>;
  /** Auto-focus hint: the work-products tab to reveal as the pipeline reaches a new
   *  stage (first design ref, first keyframe, first clip…). `focusSeq` bumps only when
   *  the stage's tab CHANGES, so the UI follows the pipeline once per stage — until the
   *  user manually pins a tab, after which auto-follow disengages. */
  focusTab: string;
  focusSeq: number;
  /** Delivery aspect from the production's config ("16:9" | "9:16"). */
  aspectRatio: string;
  /** "generative" | "footage" from the production's config. */
  productionType: string;
  /** "sheet" | "frame" from the production's config (older productions: "frame"). */
  storyboardFormat: string;
}

const finalKey = (type: string, shotId: string) =>
  `${type}:${(shotId || '').toUpperCase().replace(/\s+/g, '_').replace(/\./g, '')}`;

/** Marks a run the user cancelled, so the UI can phrase it in the active language. */
export const CANCELLED_SENTINEL = '__cancelled__';

// Which work-products step first shows an asset of a given kind — drives the
// auto-focus that follows the pipeline. Returns '' for kinds that don't map to a
// reveal-worthy step. Keys must match the rail step keys in useProductionSteps.
const AUDIO_TYPES = new Set(['dialog', 'vo', 'vo_booth', 'music', 'sfx', 'audio']);
function focusTabFor(type: string): string {
  if (type === 'character' || type === 'location' || type === 'prop') return 'design';
  if (type === 'keyframe') return 'storyboard';
  if (type === 'clip') return 'dailies';
  if (type === 'footage') return 'source';
  if (type === 'final') return 'final';
  if (AUDIO_TYPES.has(type)) return 'audio';
  return '';
}

/** Coalescing window for event-driven state refreshes (see scheduleRefresh). */
const REFRESH_COALESCE_MS = 2000;
/** While history replays, queued stream updates are applied in batches this often. */
const REPLAY_FLUSH_MS = 250;
/** Streams that never send `replay_complete` (in-process emitter) go live after this. */
const REPLAY_FALLBACK_MS = 8000;
/** Log entries kept in state — a long run has thousands; rendering them all hangs the page. */
const MAX_LOGS = 500;

const appendLog = (logs: LogEntry[], entry: LogEntry): LogEntry[] =>
  logs.length >= MAX_LOGS ? [...logs.slice(-(MAX_LOGS - 1)), entry] : [...logs, entry];

const INITIAL_STAGES: ProductionStage[] = [
  { stage: 1, name: 'Pre-Production', status: 'pending' },
  { stage: 2, name: 'Production', status: 'pending' },
  { stage: 3, name: 'Post-Production', status: 'pending' },
  { stage: 4, name: 'Delivery', status: 'pending' },
];

export async function startProduction(
  script: string,
  options?: {
    model?: string;
    aspect_ratio?: string;
    creative_brief?: string;
    attended?: boolean;
    project_id?: string;
  },
): Promise<string> {
  const { project_id, ...body } = options || {};
  const url = project_id ? `/api/projects/${project_id}/produce` : '/api/produce';

  const resp = await apiFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ script, ...body }),
  });

  if (!resp.ok) {
    throw new Error(`Server error: ${resp.status}`);
  }

  const { production_id } = await resp.json();
  return production_id;
}

function initialProductionState(productionId: string | undefined): ProductionState {
  return {
    id: productionId || null,
    status: productionId ? 'running' : 'idle',
    stages: [...INITIAL_STAGES],
    currentStage: 0,
    currentPhase: '',
    activeCrew: null,
    crewHistory: [],
    shotProgress: null,
    phaseTiming: null,
    assets: [],
    logs: [],
    error: null,
    finalVideo: null,
    scriptBreakdown: null,
    shotPlan: null,
    telemetry: null,
    availableAssets: [],
    chat: [],
    finalAssets: new Set<string>(),
    assetVersions: {},
    focusTab: '',
    focusSeq: 0,
    aspectRatio: '16:9',
    productionType: 'generative',
    storyboardFormat: 'frame',
  };
}

// Production view state lives in a Jotai atom per production (the default store),
// so leaving a production and coming back renders the last known state at once —
// the stream then RESUMES from the last event seen (no history replay into the
// cached log). Only the most recent few productions are kept.
const MAX_CACHED_PRODUCTIONS = 5;
const productionAtoms = new Map<string, PrimitiveAtom<ProductionState>>();
/** Last SSE event id received per production — where a reopened stream resumes. */
const lastEventIds = new Map<string, string>();
const productionStore = getDefaultStore();

function productionAtom(productionId: string | undefined): PrimitiveAtom<ProductionState> {
  const key = productionId || '';
  let a = productionAtoms.get(key);
  if (a) {
    productionAtoms.delete(key); // re-insert = most recently used
  } else {
    a = atom<ProductionState>(initialProductionState(productionId));
  }
  productionAtoms.set(key, a);
  while (productionAtoms.size > MAX_CACHED_PRODUCTIONS) {
    const oldest = productionAtoms.keys().next().value as string;
    productionAtoms.delete(oldest);
    lastEventIds.delete(oldest);
  }
  return a;
}

export function useProductionProgress(productionId: string | undefined) {
  const stateAtom = useMemo(() => productionAtom(productionId), [productionId]);
  const [state, setState] = useAtom(stateAtom);

  const eventSourceRef = useRef<EventSource | null>(null);
  // The Director's Log comes from the progress stream. The API's saved copy is used
  // only if the stream fails — mixing the two made the log jump between versions.
  const streamFailedRef = useRef(false);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchProductionState = useCallback(async (pid: string) => {
    try {
      const resp = await apiFetch(`/api/production/${pid}`);
      if (resp.ok) {
        const data = await resp.json();
        const v = Date.now();

        // Discover assets from the production data
        const discoveredAssets: AssetInfo[] = [];

        // Character sheets
        const charSheets = data.character_sheets || [];
        if (Array.isArray(charSheets)) {
          for (const cs of charSheets) {
            const name = (cs.name || '').toUpperCase().replace(/\s+/g, '_').replace(/\./g, '');
            discoveredAssets.push({
              type: 'character',
              shot_id: cs.name || '',
              path: `/api/assets/${pid}/characters/${name}.png?v=${v}`,
            });
          }
        }

        // Location designs
        const locDesigns = data.location_designs || [];
        if (Array.isArray(locDesigns)) {
          for (const loc of locDesigns) {
            const name = (loc.name || '').toUpperCase().replace(/\s+/g, '_').replace(/\./g, '');
            discoveredAssets.push({
              type: 'location',
              shot_id: loc.name || '',
              location_id: loc.location_id || '',
              path: `/api/assets/${pid}/locations/${name}.png?v=${v}`,
            });
          }
        }

        // Keyframes — only from GCS listing or SSE events, NOT from shot plan
        // (shot plan has planned shots that may not be generated yet)

        // Audio from manifest — use file_path from manifest (may be .wav or .mp3)
        for (const dl of data.audio_manifest?.dialog || []) {
          const ext = (dl.file_path || '').endsWith('.mp3') ? '.mp3' : '.wav';
          discoveredAssets.push({
            type: 'dialog',
            shot_id: dl.line_id,
            path: `/api/assets/${pid}/audio/dialog/${dl.line_id}${ext}?v=${v}`,
          });
        }
        for (const vo of data.audio_manifest?.vo || []) {
          const ext = (vo.file_path || '').endsWith('.mp3') ? '.mp3' : '.wav';
          discoveredAssets.push({
            type: 'vo',
            shot_id: vo.line_id,
            path: `/api/assets/${pid}/audio/vo/${vo.line_id}${ext}?v=${v}`,
          });
        }

        // Clips — only from GCS listing or SSE events, NOT from resolved_shots
        // (resolved_shots lists planned clips that may not be generated yet)

        // Discover additional assets from GCS listing (skip if already found from JSON)
        const gcsAssets: string[] = data.available_assets || [];
        const hasAsset = (type: string, name: string) =>
          discoveredAssets.some((a) => a.type === type && (a.shot_id === name || a.path.includes(name)));

        for (const assetPath of gcsAssets) {
          if (assetPath.startsWith('characters/') && assetPath.endsWith('.png')) {
            const name = assetPath.replace('characters/', '').replace('.png', '');
            if (!hasAsset('character', name)) {
              discoveredAssets.push({ type: 'character', shot_id: name, path: `/api/assets/${pid}/${assetPath}?v=${v}` });
            }
          } else if (assetPath.startsWith('locations/') && assetPath.endsWith('.png')) {
            const name = assetPath.replace('locations/', '').replace('.png', '');
            if (!hasAsset('location', name)) {
              discoveredAssets.push({ type: 'location', shot_id: name, path: `/api/assets/${pid}/${assetPath}?v=${v}` });
            }
          } else if (assetPath.startsWith('props/') && assetPath.endsWith('.png')) {
            const name = assetPath.replace('props/', '').replace('.png', '');
            if (!hasAsset('prop', name)) {
              discoveredAssets.push({ type: 'prop', shot_id: name, path: `/api/assets/${pid}/${assetPath}?v=${v}` });
            }
          } else if (assetPath.startsWith('footage/thumbs/') && assetPath.endsWith('.png')) {
            const name = assetPath.replace('footage/thumbs/', '').replace('.png', '');
            if (!hasAsset('footage', name)) {
              discoveredAssets.push({ type: 'footage', shot_id: name, path: `/api/assets/${pid}/${assetPath}?v=${v}` });
            }
          } else if (assetPath.startsWith('keyframes/') && assetPath.endsWith('.png')) {
            const id = assetPath.replace('keyframes/', '').replace('.png', '');
            if (!hasAsset('keyframe', id)) {
              discoveredAssets.push({ type: 'keyframe', shot_id: id, path: `/api/assets/${pid}/${assetPath}?v=${v}` });
            }
          } else if (assetPath.startsWith('clips/') && assetPath.endsWith('.mp4')) {
            const id = assetPath.replace('clips/', '').replace('.mp4', '');
            // Chain intermediates (_segN/_concat) are build artifacts, never assets.
            if (/_seg\d+$|_concat$/.test(id)) continue;
            if (!hasAsset('clip', id)) {
              discoveredAssets.push({ type: 'clip', shot_id: id, path: `/api/assets/${pid}/${assetPath}?v=${v}` });
            }
          } else if (assetPath.startsWith('audio/vo_booth/') && assetPath.endsWith('.mp4')) {
            const id = assetPath.replace('audio/vo_booth/', '').replace('.mp4', '');
            if (!hasAsset('vo_booth', id)) {
              discoveredAssets.push({ type: 'vo_booth', shot_id: id, path: `/api/assets/${pid}/${assetPath}?v=${v}` });
            }
          } else if (assetPath.startsWith('audio/vo/')) {
            const id = assetPath.replace('audio/vo/', '').replace('.wav', '').replace('.mp3', '');
            if (!hasAsset('vo', id)) {
              discoveredAssets.push({ type: 'vo', shot_id: id, path: `/api/assets/${pid}/${assetPath}?v=${v}` });
            }
          } else if (assetPath.startsWith('audio/music/')) {
            const id = assetPath.replace('audio/music/', '').replace('.mp3', '');
            if (!hasAsset('music', id)) {
              discoveredAssets.push({ type: 'music', shot_id: id, path: `/api/assets/${pid}/${assetPath}?v=${v}` });
            }
          }
        }

        // Final video
        const title = (data.script_breakdown?.title || 'PRODUCTION').toUpperCase().replace(/\s+/g, '_');
        const finalPath = `/api/assets/${pid}/final/${title}.mp4`;
        const hasFinal = gcsAssets.some((a: string) => a.startsWith('final/') && a.endsWith('.mp4'));

        // Acceptance record: asset_ready events in the saved Director's Log mark
        // assets that PASSED QC (iteration finished). Parse from the FULL log
        // before slicing so finality survives reloads and instance recycles.
        const savedFinal = new Set<string>();
        // Preferred: server-computed union of the full saved + live logs (survives
        // instance recycles and the 400-entry log cap).
        if (Array.isArray(data.final_assets)) {
          for (const fa of data.final_assets) {
            if (fa?.type && fa?.shot_id) savedFinal.add(finalKey(fa.type, fa.shot_id));
          }
        }
        if (Array.isArray(data.log)) {
          for (const e of data.log) {
            if (!e?.data?.type || !e.data?.shot_id) continue;
            // A later asset_updated (a sent-back frame's new attempt) is iterating again.
            if (e.event === 'asset_ready') savedFinal.add(finalKey(e.data.type, e.data.shot_id));
            else if (e.event === 'asset_updated') savedFinal.delete(finalKey(e.data.type, e.data.shot_id));
          }
        }

        // Keep only the tail — a long run produces thousands of log entries; holding
        // them all in state (and re-rendering) makes Chrome unresponsive.
        const savedLog: LogEntry[] = Array.isArray(data.log)
          ? data.log.slice(-500).map((e: { event: string; data: { message?: string }; timestamp: number }) => ({
              event: e.event,
              message: e.data?.message || '',
              timestamp: e.timestamp * 1000,
            }))
          : [];

        setState((prev) => {
          // Merge: keep existing assets (stable URLs), only ADD truly new ones.
          // Normalize the key: SSE events carry display names ("Research Commons")
          // while GCS discovery carries file names ("RESEARCH_COMMONS") — without
          // normalization every character/location shows twice.
          const assetKey = (a: AssetInfo) => `${a.type}:${a.shot_id.toUpperCase().replace(/\s+/g, '_').replace(/\./g, '')}`;
          const mergedAssets = [...prev.assets];
          const indexByKey = new Map(mergedAssets.map((a, i) => [assetKey(a), i]));
          for (const newAsset of discoveredAssets) {
            const key = assetKey(newAsset);
            const at = indexByKey.get(key);
            if (at === undefined) {
              indexByKey.set(key, mergedAssets.length);
              mergedAssets.push(newAsset);
            } else {
              // UPGRADE, don't drop: the manifest duplicate carries location_id and the
              // display name, while a GCS-discovered one has only the filename.
              const cur = mergedAssets[at];
              const richer = (!cur.location_id && newAsset.location_id) || (/_/.test(cur.shot_id) && /\s/.test(newAsset.shot_id));
              if (richer) {
                mergedAssets[at] = {
                  ...cur,
                  shot_id: newAsset.shot_id,
                  location_id: newAsset.location_id || cur.location_id,
                };
              }
            }
          }

          // Use the longer log (API saved vs SSE streamed)
          const bestLog = streamFailedRef.current && savedLog.length > prev.logs.length ? savedLog : prev.logs;

          // Map status from Firestore
          const apiStatus = data.status;
          let uiStatus = prev.status;
          if (apiStatus === 'complete') uiStatus = 'complete';
          else if (apiStatus === 'error' || apiStatus === 'cancelled' || apiStatus === 'failed') uiStatus = 'error';
          else if (apiStatus === 'running') uiStatus = 'running';

          const mergedFinal = new Set(prev.finalAssets);
          for (const k of savedFinal) mergedFinal.add(k);

          return {
            ...prev,
            finalAssets: mergedFinal,
            scriptBreakdown: data.script_breakdown || prev.scriptBreakdown,
            shotPlan: data.shot_plan || prev.shotPlan,
            status: uiStatus,
            // Sentinel, not display text: the header maps CANCELLED_SENTINEL to a
            // translated string. Baking English here would leak into the Spanish UI.
            error: apiStatus === 'cancelled' ? CANCELLED_SENTINEL : data.error_message || data.error || prev.error,
            assets: mergedAssets,
            finalVideo: hasFinal ? finalPath : prev.finalVideo,
            telemetry: data.telemetry || prev.telemetry,
            logs: bestLog,
            availableAssets: gcsAssets.length > 0 ? gcsAssets : prev.availableAssets,
            aspectRatio: data.config?.aspect_ratio || prev.aspectRatio,
            productionType: data.config?.production_type || prev.productionType,
            storyboardFormat: data.config ? data.config.storyboard_format || 'frame' : prev.storyboardFormat,
          };
        });
      }
    } catch {
      // Non-critical
    }
  }, []);

  useEffect(() => {
    if (!productionId) return;

    // Resume from the last event this tab saw (cached state is current up to it);
    // without one, start clean so the stream's history replay doesn't duplicate logs.
    const resumeFrom = lastEventIds.get(productionId) || '';
    if (!resumeFrom) productionStore.set(stateAtom, initialProductionState(productionId));
    setState((prev) => ({ ...prev, id: productionId }));

    // Load persisted state from API first (database is source of truth)
    fetchProductionState(productionId);

    // Poll API every 30s as backup for missed SSE events
    const pollInterval = setInterval(() => {
      fetchProductionState(productionId);
    }, 30000);

    let es: EventSource | null = null;
    let cancelled = false;
    streamFailedRef.current = false;

    // While the stream REPLAYS history, queue state updates and apply them in a few
    // batched renders: one render per replayed event made a long run's page (crew,
    // Director's Log) keep changing for a minute. Live events apply immediately.
    type Update = (s: ProductionState) => ProductionState;
    let replaying = true;
    const queued: Update[] = [];
    let flushTimer: ReturnType<typeof setTimeout> | null = null;
    const flush = () => {
      flushTimer = null;
      if (cancelled || !queued.length) return;
      const fns = queued.splice(0);
      setState((prev) => fns.reduce((acc, fn) => fn(acc), prev));
    };
    const update = (fn: Update) => {
      if (!replaying) {
        setState(fn);
        return;
      }
      queued.push(fn);
      if (!flushTimer) flushTimer = setTimeout(flush, REPLAY_FLUSH_MS);
    };
    const goLive = () => {
      if (!replaying) return;
      replaying = false;
      if (flushTimer) clearTimeout(flushTimer);
      flush();
    };
    const replayFallback = setTimeout(goLive, REPLAY_FALLBACK_MS);

    // Event-driven refreshes are COALESCED: at most one state fetch per window.
    // On (re)connect the progress stream replays the run's history in a burst —
    // a long run carries hundreds of crew_active events, and fetching per event
    // fired hundreds of /api/production requests at once.
    const scheduleRefresh = () => {
      if (refreshTimerRef.current) return;
      refreshTimerRef.current = setTimeout(() => {
        refreshTimerRef.current = null;
        if (!cancelled) fetchProductionState(productionId);
      }, REFRESH_COALESCE_MS);
    };

    const connect = async () => {
      const base = await sseUrl(`/api/progress/${productionId}`);
      if (cancelled) return;
      const url = resumeFrom ? `${base}${base.includes('?') ? '&' : '?'}last_event_id=${encodeURIComponent(resumeFrom)}` : base;

      es = new EventSource(url);
      eventSourceRef.current = es;
      const source = es;
      // Every listener goes through here so the last event id is remembered.
      const on = (name: string, handler: (e: MessageEvent) => void) =>
        source.addEventListener(name, (e) => {
          const me = e as MessageEvent;
          if (me.lastEventId) lastEventIds.set(productionId, me.lastEventId);
          handler(me);
        });

      es.onerror = () => {
        if (es?.readyState === EventSource.CLOSED) {
          streamFailedRef.current = true;
          goLive();
          fetchProductionState(productionId);
        }
      };

      // Everything after this marker is live (see the replay queue above).
      on('replay_complete', goLive);

      on('stage_start', (e) => {
        const data = JSON.parse(e.data);
        update((prev) => ({
          ...prev,
          currentStage: data.stage,
          stages: prev.stages.map((s) =>
            s.stage === data.stage
              ? { ...s, status: 'active' as const, model: data.model || undefined }
              : s.stage < data.stage
                ? { ...s, status: 'complete' as const }
                : s,
          ),
          logs: appendLog(prev.logs, { event: 'stage_start', message: `${data.name} started`, timestamp: Date.now() }),
        }));
      });

      on('crew_active', (e) => {
        const data = JSON.parse(e.data);
        const phaseMap: Record<string, number> = {
          'pre-production': 1,
          production: 2,
          'post-production': 3,
          delivery: 4,
        };
        const stageNum = phaseMap[data.phase] || 0;
        update((prev) => {
          const newStages = prev.stages.map((s) => {
            if (s.stage === stageNum) return { ...s, status: 'active' as const };
            if (s.stage < stageNum && s.status !== 'complete') return { ...s, status: 'complete' as const };
            return s;
          });
          return {
            ...prev,
            activeCrew: { role: data.role, phase: data.phase, task: data.task },
            currentPhase: data.phase,
            crewHistory: prev.crewHistory.some((c) => c.role === data.role && c.task === data.task)
              ? prev.crewHistory
              : [...prev.crewHistory, { role: data.role, phase: data.phase, task: data.task }],
            stages: newStages,
          };
        });
        scheduleRefresh();
      });

      on('stage_complete', (e) => {
        const data = JSON.parse(e.data);
        update((prev) => ({
          ...prev,
          stages: prev.stages.map((s) => (s.stage === data.stage ? { ...s, status: 'complete' as const } : s)),
          logs: appendLog(prev.logs, { event: 'stage_complete', message: `${data.name} complete`, timestamp: Date.now() }),
        }));
        scheduleRefresh();
      });

      // The server ends the stream once the production is no longer live. Close
      // here — otherwise EventSource auto-reconnects and the stream starts over.
      on('stream_end', () => {
        es?.close();
        fetchProductionState(productionId);
      });

      on('shot_progress', (e) => {
        const data = JSON.parse(e.data);
        update((prev) => {
          // Track per-phase timing for the ETA. A change of `step` (storyboard →
          // shooting → …) starts a fresh clock so each phase is estimated from
          // its OWN item rate, never a blend across phases.
          const now = Date.now();
          // Prefer the SERVER emit time so a burst replay on (re)connect still
          // reflects the TRUE cadence; fall back to the client clock if absent.
          const ts = typeof data._ts === 'number' ? data._ts : now;
          const pt = prev.phaseTiming;
          // `current` is now a monotonic count of frames FINISHED. The engine also
          // re-emits the same `current` at each shot START (for the "now working
          // on…" label) — those carry NO advance, so anchor the rate clock ONLY to
          // events where `current` actually increases. Otherwise a burst of start-
          // emits would drag updatedTs forward without a completion and inflate the
          // per-item time.
          const advanced = !!pt && pt.step === data.step && data.current > pt.current;
          const phaseTiming: PhaseTiming =
            !pt || pt.step !== data.step
              ? {
                  step: data.step,
                  startedTs: ts,
                  startCurrent: data.current,
                  current: data.current,
                  total: data.total_shots,
                  updatedTs: ts,
                  updatedAt: now,
                }
              : advanced
                ? {
                    ...pt,
                    current: data.current,
                    total: data.total_shots,
                    updatedTs: ts,
                    updatedAt: now,
                  }
                : { ...pt, total: data.total_shots };
          return { ...prev, shotProgress: data, phaseTiming };
        });
      });

      on('asset_ready', (e) => {
        // asset_ready fires on ACCEPTANCE — record finality for the tile badges.
        try {
          const d = JSON.parse((e as MessageEvent).data);
          if (d?.type && d?.shot_id) {
            update((prev) => {
              const k = finalKey(d.type, d.shot_id);
              if (prev.finalAssets.has(k)) return prev;
              const next = new Set(prev.finalAssets);
              next.add(k);
              return { ...prev, finalAssets: next };
            });
          }
        } catch {
          // ignore
        }
        const data = JSON.parse(e.data);
        // A re-fire during the REPLAY is history, not a regeneration — bumping on it
        // changed each <video> src hundreds of times (a run that re-announced its
        // clips on every shoot pass), cancelling and restarting the loads.
        const live = !replaying;
        update((prev) => {
          // Dedup with the SAME normalized key the poll/GCS merge uses (:309) —
          // a raw `type:shot_id` here vs a normalized key there let two entries
          // for the same shot coexist, and the grid (Map, last-wins) and the
          // lightbox (find, first-wins) then rendered different images.
          const norm = (s: string) => s.toUpperCase().replace(/\s+/g, '_').replace(/\./g, '').replace(/\//g, '_');
          const vk = norm(String(data.shot_id || ''));
          const key = `${data.type}:${vk}`;
          const known = prev.assets.some((a) => `${a.type}:${norm(a.shot_id)}` === key);
          // Replayed re-announcements of an asset we already show change nothing (each
          // carries its own ?v=, which would still swap the <video>/<img> src).
          if (!live && known) return prev;
          const existed = live && known;
          const filtered = prev.assets.filter((a) => `${a.type}:${norm(a.shot_id)}` !== key);
          // A re-fire means the file was REGENERATED — bump its version so tiles
          // refetch instead of showing the stale cached image.
          const assetVersions = existed ? { ...prev.assetVersions, [vk]: (prev.assetVersions[vk] || 0) + 1 } : prev.assetVersions;
          // Cache-bust the URL: full-res images are served with max-age=3600
          // (server.py), so without this a regenerated frame keeps showing the
          // browser-cached predecessor for up to an hour.
          const bust = existed ? (prev.assetVersions[vk] || 0) + 1 : 0;
          const busted = data.path + (String(data.path).includes('?') ? '&' : '?') + 'g=' + bust;
          // Auto-focus: bump the seq only when this asset belongs to a DIFFERENT tab
          // than the last focused one, so we jump once per pipeline stage, not per asset.
          const ft = focusTabFor(String(data.type || ''));
          const focusChanged = ft && ft !== prev.focusTab;
          return {
            ...prev,
            assetVersions,
            assets: [...filtered, { type: data.type, shot_id: data.shot_id, path: busted }],
            focusTab: ft || prev.focusTab,
            focusSeq: focusChanged ? prev.focusSeq + 1 : prev.focusSeq,
          };
        });
      });

      // A best-of-N attempt replaced the file but isn't accepted yet: swap the tile to
      // the current attempt (fresh ?v=) WITHOUT marking it final, so it keeps its
      // "iterating" chip until asset_ready. Replayed ones are history — skip them.
      on('asset_updated', (e) => {
        if (replaying) return;
        const data = JSON.parse(e.data);
        if (!data?.type || !data?.shot_id || !data?.path) return;
        update((prev) => {
          const key = `${data.type}:${normKey(String(data.shot_id))}`;
          const others = prev.assets.filter((a) => `${a.type}:${normKey(a.shot_id)}` !== key);
          // An accepted asset sent back for rework is iterating again until its next asset_ready.
          const fk = finalKey(data.type, data.shot_id);
          let finalAssets = prev.finalAssets;
          if (finalAssets.has(fk)) {
            finalAssets = new Set(finalAssets);
            finalAssets.delete(fk);
          }
          return {
            ...prev,
            finalAssets,
            assets: [...others, { type: data.type, shot_id: data.shot_id, path: data.path }],
          };
        });
      });

      on('telemetry', (e) => {
        const data = JSON.parse(e.data);
        update((prev) => ({ ...prev, telemetry: data }));
      });

      on('cuttingroom_ready', (e) => {
        const data = JSON.parse(e.data);
        if (data.url) {
          // Do NOT auto-open a tab — this event re-fires on every refresh/reconnect,
          // spawning an unwanted CuttingRoom tab each time. Just surface the link.
          update((prev) => ({
            ...prev,
            logs: appendLog(prev.logs, {
              event: 'log',
              message: `CuttingRoom project ready: ${data.title} — ${data.url}`,
              timestamp: Date.now(),
            }),
          }));
        }
      });

      on('log', (e) => {
        const data = JSON.parse(e.data);
        update((prev) => ({
          ...prev,
          logs: appendLog(prev.logs, { event: 'log', message: data.message, timestamp: Date.now() }),
        }));
      });

      on('chat', (e) => {
        const data = JSON.parse(e.data);
        update((prev) => {
          const msg: ChatMessage = {
            id: data.message_id || `${data.role}-${Date.now()}-${Math.random()}`,
            role: data.role,
            text: data.text,
            kind: data.kind || 'message',
            action: data.action || null,
            asset_ref: data.asset_ref || '',
            // A proposal auto-confirmed at an open gate arrives with an explicit
            // status; otherwise a proposal defaults to awaiting_confirm.
            status: data.status || (data.kind === 'proposal' ? 'awaiting_confirm' : 'done'),
            timestamp: Date.now(),
            turn: data.turn || undefined,
            reply_to: data.reply_to || undefined,
            choices: Array.isArray(data.choices) ? data.choices : undefined,
            attachments: Array.isArray(data.attachments) ? data.attachments : undefined,
            steps: Array.isArray(data.steps) ? data.steps : undefined,
          };
          // Dedupe by real message_id (SSE replay); producer echoes have none.
          if (data.message_id && prev.chat.some((m) => m.id === data.message_id)) {
            return prev;
          }
          return { ...prev, chat: [...prev.chat, msg] };
        });
      });

      on('error', (e) => {
        try {
          const data = JSON.parse((e as MessageEvent).data);
          update((prev) => ({
            ...prev,
            logs: appendLog(prev.logs, { event: 'error', message: data.message, timestamp: Date.now() }),
          }));
        } catch {
          // SSE connection error
        }
      });

      on('complete', (e) => {
        const data = JSON.parse(e.data);
        const hasVideo = !!data.final_video;
        update((prev) => ({
          ...prev,
          status: hasVideo ? 'complete' : 'error',
          finalVideo: data.final_video || null,
          stages: hasVideo ? prev.stages.map((s) => ({ ...s, status: 'complete' as const })) : prev.stages,
          logs: appendLog(prev.logs, {
            event: 'complete',
            message: hasVideo ? 'Production complete' : 'Production ended with errors',
            timestamp: Date.now(),
          }),
        }));
        // Only a LIVE completion closes the stream: a `complete` replayed from the
        // history of a production that was later resumed must not cut the live
        // updates (the server sends `stream_end` when the run is really over).
        if (!replaying) es?.close();
        fetchProductionState(productionId);
      });
    };

    connect();

    return () => {
      cancelled = true;
      es?.close();
      clearInterval(pollInterval);
      clearTimeout(replayFallback);
      if (flushTimer) clearTimeout(flushTimer);
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [productionId, fetchProductionState, stateAtom, setState]);

  return state;
}
